import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scale } from "lucide-react";

const r3 = (n) => Math.round(n * 1000) / 1000;

export const equalSplit = (total, n) => {
  const each = r3(total / n);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? r3(total - each * (n - 1)) : each));
};

// Weight-UOM lines (KG, TON…): choose the number of stickers and edit each piece's weight.
export function WeightSplitPanel({ lines, splits, onApply, loading }) {
  const [draft, setDraft] = useState({});
  useEffect(() => {
    const d = {};
    lines.forEach((l) => { d[l.item_no] = (splits[l.item_no] || [l.qty]).map(String); });
    setDraft(d);
  }, [lines, splits]);

  if (!lines.length) return null;
  const setCount = (l, v) => {
    const n = Math.max(1, Math.min(500, parseInt(v, 10) || 1));
    setDraft((d) => ({ ...d, [l.item_no]: equalSplit(Number(l.qty), n).map(String) }));
  };
  const setWeight = (l, i, v) => setDraft((d) => ({ ...d, [l.item_no]: d[l.item_no].map((w, k) => (k === i ? v : w)) }));
  const apply = () => {
    const out = {};
    Object.entries(draft).forEach(([k, ws]) => { if (ws.length > 1) out[k] = ws.map(Number); });
    onApply(out);
  };

  return (
    <div className="no-print rounded-lg border border-sky-200 bg-sky-50/40 p-4 shadow-sm space-y-3" data-testid="weight-split-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Scale className="h-4 w-4 text-sky-700" />Weight lines — how many stickers?</p>
        <Button size="sm" onClick={apply} disabled={loading} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="weight-split-apply">Apply stickers</Button>
      </div>
      <p className="text-xs text-slate-500">Weight is split equally by default — edit any piece's weight (e.g. each plate). One sticker prints the full line weight.</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
      {lines.map((l) => {
        const ws = draft[l.item_no] || [String(l.qty)];
        const sum = r3(ws.reduce((a, w) => a + (Number(w) || 0), 0));
        const off = ws.length > 1 && Math.abs(sum - Number(l.qty)) > 0.0005;
        return (
          <div key={l.item_no} className="rounded-md border border-slate-200 bg-white px-3 py-2 space-y-2" data-testid={`weight-split-line-${l.item_no}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="text-[11px] text-slate-400 tabular-nums">#{l.item_no}</span>
              <span className="font-mono font-bold text-slate-900">{l.material}</span>
              <span className="text-slate-500 truncate max-w-[200px]" title={l.description}>{l.description}</span>
              <span className="text-slate-500">Line qty: <b className="text-slate-800 tabular-nums">{l.qty} {l.unit}</b></span>
              <label className="flex items-center gap-1.5 ml-auto text-xs text-slate-600">No. of stickers
                <Input type="number" min="1" max="500" value={ws.length} onChange={(e) => setCount(l, e.target.value)}
                  className="h-8 w-20 text-center text-sm" data-testid={`weight-split-count-${l.item_no}`} />
              </label>
            </div>
            {ws.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {ws.map((w, i) => (
                  <label key={i} className="flex items-center gap-1 text-[11px] text-slate-500">{i + 1}.
                    <Input type="number" min="0" step="any" value={w} onChange={(e) => setWeight(l, i, e.target.value)}
                      className="h-7 w-24 text-right text-xs tabular-nums" data-testid={`weight-split-w-${l.item_no}-${i + 1}`} />
                  </label>
                ))}
              </div>
            )}
            {off && <p className="text-[11px] text-amber-700" data-testid={`weight-split-warn-${l.item_no}`}>Piece weights add up to {sum} {l.unit}, line qty is {l.qty} {l.unit}.</p>}
          </div>
        );
      })}
      </div>
    </div>
  );
}
